/** Stable capability keys — must match DB service / Insights / Orchestrator. */
export const CAPABILITY_KEYS = {
  INDUSTRIES: 'industries',
  INDUSTRY_NARRATIVE_RADAR: 'industry_narrative_radar',
  ASSETS: 'assets',
  ACCOUNTS: 'accounts',
  ACCOUNT_SIGNALS: 'account_signals',
  ACCOUNT_NARRATIVE_RADAR: 'account_narrative_radar',
  HOTLIST: 'hotlist',
  CADENCES: 'cadences',
  CONTACT_ENRICHMENT_LIGHT: 'contact_enrichment_light',
  CONTACT_ENRICHMENT_FULL: 'contact_enrichment_full',
  CRM_SYNC: 'crm_sync',
} as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[keyof typeof CAPABILITY_KEYS];

/** Hotlist and cadences require at least one of these module surfaces. */
export const MODULE_SURFACE_ANY_OF_PARENTS: CapabilityKey[] = [
  CAPABILITY_KEYS.ACCOUNTS,
  CAPABILITY_KEYS.ASSETS,
  CAPABILITY_KEYS.INDUSTRIES,
];
export const HOTLIST_ANY_OF_PARENTS = MODULE_SURFACE_ANY_OF_PARENTS;
export const CADENCES_ANY_OF_PARENTS = MODULE_SURFACE_ANY_OF_PARENTS;

export const ANY_OF_DEPENDENT_KEYS: CapabilityKey[] = [
  CAPABILITY_KEYS.HOTLIST,
  CAPABILITY_KEYS.CADENCES,
];

export const HARD_PARENTS: Partial<Record<CapabilityKey, CapabilityKey>> = {
  [CAPABILITY_KEYS.INDUSTRY_NARRATIVE_RADAR]: CAPABILITY_KEYS.INDUSTRIES,
  [CAPABILITY_KEYS.ACCOUNT_SIGNALS]: CAPABILITY_KEYS.ACCOUNTS,
  [CAPABILITY_KEYS.ACCOUNT_NARRATIVE_RADAR]: CAPABILITY_KEYS.ACCOUNTS,
  [CAPABILITY_KEYS.CONTACT_ENRICHMENT_LIGHT]: CAPABILITY_KEYS.ACCOUNTS,
  [CAPABILITY_KEYS.CONTACT_ENRICHMENT_FULL]: CAPABILITY_KEYS.ACCOUNTS,
  [CAPABILITY_KEYS.CRM_SYNC]: CAPABILITY_KEYS.ACCOUNTS,
};

export function applyCapabilityClosures(
  flags: Record<string, boolean>
): Record<string, boolean> {
  const next = { ...flags };

  for (const [child, parent] of Object.entries(HARD_PARENTS)) {
    if (next[child] && parent && !next[parent]) {
      next[child] = false;
    }
  }

  for (const key of ANY_OF_DEPENDENT_KEYS) {
    if (next[key] && !MODULE_SURFACE_ANY_OF_PARENTS.some((parent) => next[parent])) {
      next[key] = false;
    }
  }

  if (next[CAPABILITY_KEYS.CONTACT_ENRICHMENT_FULL]) {
    next[CAPABILITY_KEYS.CONTACT_ENRICHMENT_LIGHT] = true;
  }

  return next;
}
